import { ActivityTitle } from '../activity-panel';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { Box, Divider, Typography } from '@mui/material';
import Dropzone from 'components/common/dropzone/Dropzone';
import { useState } from 'react';
import { attachFiles, catchErrorMessage } from 'utility/helpers';
import RecentFiles from './RecentFiles';
import useFilesQuery, { FileType } from '@buildwise/pages/companyFiles/hooks/useFilesQuery';
import UploadFilesWrapper from '@buildwise/pages/companyFiles/components/UploadFilesWrapper';
import moment from 'moment';
import useUserData from 'hooks/useUserData';
import { uniqueId } from 'lodash';
import { PythonProceduresAPI } from '@buildwise/libs/procedures';
import { toast } from 'react-toastify';

export type UploadingFileType = {
  documentName: string;
  documentType: string;
  uploadedPercentage: number;
  createdAt: Date;
};

function CompanyFilesWidget() {
  const { user, companyId } = useUserData();
  const { proceduresModuleName } = useBuildWiseModules();
  const { files, updateFilesCache, refetch } = useFilesQuery({ level: 'company' });
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, UploadingFileType>>({});

  const uploadFiles = async (files: File[]) => {
    let mappedFiles: FileType[] = [];
    const getFileDirectory = (file: File & { path?: string }) => {
      const directory = `company/${companyId}/files`;
      if (file.path && file.path?.replace(file.name, '')) {
        return `${file.path.replace(`/${file.name}`, '')}`;
      }

      if (file.webkitRelativePath) {
        return `${file.webkitRelativePath.replace(`/${file.name}`, '')}`;
      }

      return directory;
    };

    await attachFiles({
      newAttachments: files,
      attachments: [],
      setAttachments: (_files) => {
        mappedFiles = _files.map((file) => ({
          createdAt: moment().toISOString(),
          createdBy: user?.username || '',
          documentDirectory: getFileDirectory(file.file),
          documentPath: getFileDirectory(file.file),
          documentName: file.fileName,
          documentPDF: '',
          documentSize: file.fileSize,
          documentType: file.fileName.split('.').pop() || '',
          documentURL: file.articleOpenUrl || '',
          id: uniqueId(),
          profileImageURL: user?.profileImageURL || '',
          userNameExpYearsToDisplay: user?.expYears || '',
          userNameTitleToDisplay: user?.title || '',
          userNameToDisplay: user?.userNameToDisplay || '',
          file: file.file,
          fileMD5Hash: file.fileMD5Hash,
        }));
      },
      replace: false,
      acceptedFiles: [],
    });

    const _uploadingFiles: Record<string, UploadingFileType> = {};
    mappedFiles.forEach((file) => {
      _uploadingFiles[file.documentName] = {
        documentName: file.documentName,
        documentType: file.documentType,
        uploadedPercentage: 0,
        createdAt: new Date(),
      };
    });

    setUploadingFiles((prevState) => ({
      ...prevState,
      ..._uploadingFiles,
    }));

    try {
      // Upload files meta info
      const { data } = await PythonProceduresAPI.saveFilesMetaData({
        filesList: mappedFiles.map((file) => ({
          documentName: file.documentName,
          documentDirectory: file.documentDirectory,
          documentSize: file.documentSize,
          documentType: file.documentType,
          projectId: file.projectId,
        })),
        companyId,
        level: 'company',
      });

      // Upload files to S3
      if (data?.filesList && Array.isArray(data.filesList)) {
        mappedFiles = mappedFiles.map((file) => ({
          ...file,
          ...data.filesList.find((_file) => _file.documentName === file.documentName),
        }));
      }
      const filesUploadPromises: Promise<any>[] = [];

      mappedFiles.forEach((file) => {
        filesUploadPromises.push(
          new Promise(async (resolve, reject) => {
            try {
              if (file.UploadUrl && file.file) {
                await PythonProceduresAPI.uploadFileToS3({
                  s3Url: file.UploadUrl,
                  file: file.file,
                  uploadProgress: (uploadedPercentage) => {
                    setUploadingFiles((prevState) => ({
                      ...prevState,
                      [file.documentName]: {
                        ...prevState[file.documentName],
                        uploadedPercentage,
                      },
                    }));
                  },
                });

                // Verify uploaded files are saved
                if (file.fileMD5Hash) {
                  await PythonProceduresAPI.verifyUploadedFiles({
                    id: file.id,
                    companyId,
                    fileMD5Hash: file.fileMD5Hash,
                    MimeType: file.file.type,
                  });

                  setUploadingFiles((prevState) => {
                    const newState = { ...prevState };
                    delete newState[file.documentName];
                    return newState;
                  });
                  resolve(true);
                }
              }
            } catch (error) {
              console.log('S3_FILE_UPLOAD_ERROR', error);
              reject(error);
            }
          }),
        );
      });

      if (filesUploadPromises.length > 0) {
        await Promise.all(filesUploadPromises);
        // Update procedures cahce
        updateFilesCache({
          action: 'add',
          files: mappedFiles,
        });
        // Remove this unnecessary refetch once verfiy files API is returning documentURL in the response.
        refetch();
      }
    } catch (error) {
      console.log('FOLDER_UPLOAD_ERROR', error);
      toast.error(catchErrorMessage(error));
    } finally {
      setUploadingFiles({});
    }
  };

  return (
    <Box pb={1}>
      <ActivityTitle as="h3" sx={{ p: 2, pb: 0 }}>
        {proceduresModuleName}
      </ActivityTitle>

      <UploadFilesWrapper uploadFiles={uploadFiles} sx={{ px: 2 }}>
        <Box mt={1} mb={2}>
          <Typography
            sx={(theme) => ({
              color: theme.palette.custom.tuna,
              fontSize: 14,
              mb: 2,
            })}
          >
            Upload files silently — no posts or notifications. Others can find them in search
            results.
          </Typography>

          <Dropzone
            onDrop={uploadFiles}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px dashed ${theme.palette.custom.coolGrey}`,
            })}
            dropzonePlaceholder="Drop files here"
          />
        </Box>
        {files.length > 0 && (
          <>
            <Divider />
            <Box mt={1}>
              <RecentFiles uploadingFiles={Object.values(uploadingFiles)} />
            </Box>
          </>
        )}
      </UploadFilesWrapper>
    </Box>
  );
}

export default CompanyFilesWidget;
